'use client';

import { createSecureInformationRequest } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToggle,
} from '@commenda/kakashi';
import { toOrdinal } from '@commenda/utils';
import { SecureInformationRequestFieldType } from '@prisma/client';
import { useNotification } from '../../hooks/useNotification';
import { GroupProps } from './types';

type SendSecureInformationRequestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  businessEntityName: string;
  corporationId: number;
  groups: GroupProps[];
  title: string;
  linkedServiceRequestId: string | null;
};

const SendSecureInformationRequestModal = ({
  isOpen,
  onClose,
  onConfirm,
  businessEntityName,
  corporationId,
  groups,
  title,
  linkedServiceRequestId,
}: SendSecureInformationRequestModalProps) => {
  const isSendingRequest = useToggle();

  const { setNotification } = useNotification();

  const onSendRequest = async () => {
    isSendingRequest.on();

    for (let g = 0; g < groups.length; g += 1) {
      const group = groups[g];
      const groupName = group.headerText?.trim();
      if (!groupName) {
        setNotification({
          show: true,
          valid: false,
          title: 'Missing group name',
          message: `Please provide a header for ${toOrdinal(g + 1)} group.`,
        });
        isSendingRequest.off();
        return;
      }

      for (let f = 0; f < group.fields.length; f += 1) {
        const field = group.fields[f];
        const fieldHeader = field.headerText?.trim();

        if (!fieldHeader) {
          setNotification({
            show: true,
            valid: false,
            title: 'Missing field header',
            message: `Please enter a header for ${toOrdinal(f + 1)} field, for group "${groupName}".`,
          });
          isSendingRequest.off();
          return;
        }
        if (!field.type) {
          setNotification({
            show: true,
            valid: false,
            title: 'Missing field type',
            message: `Please select a type for ${toOrdinal(f + 1)} field, for group "${groupName}".`,
          });
          isSendingRequest.off();
          return;
        }
      }
    }

    const res = await createSecureInformationRequest(
      {
        corporationId,
        groups: groups.map((group, idx) => {
          return {
            headerText: group.headerText,
            indexInRequest: idx + 1,
            fields: group.fields.map((field, index) => {
              return {
                headerText: field.headerText,
                type: field.type,
                isSecure: field.isSecure,
                indexInGroup: index + 1,
                ...(field.type === SecureInformationRequestFieldType.SELECT ||
                field.type === SecureInformationRequestFieldType.MULTI_SELECT
                  ? { selectOptions: field.selectOptions }
                  : {}),
                ...(field.tooltip ? { tooltipText: field.tooltip } : {}),
              };
            }),
          };
        }),
        title,
        ...(linkedServiceRequestId
          ? { serviceRequestId: linkedServiceRequestId }
          : {}),
      },
      undefined
    );

    if (!res.ok) {
      setNotification({
        show: true,
        title: 'Error!',
        message: 'Failed to create secure information request.',
        valid: false,
      });
      isSendingRequest.off();
      return;
    }

    setNotification({
      show: true,
      title: 'Secure information request sent!',
      message: 'Your client will be notified via email.',
      valid: true,
    });

    isSendingRequest.off();

    onConfirm();
  };

  const isLoading = isSendingRequest.value;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader className="flex flex-col items-start">
          <DialogTitle>Are you sure?</DialogTitle>
        </DialogHeader>

        <div className="p-3xl">
          <p className="txt-medium-regular text-text-secondary">
            You are about to send a secure information request to{' '}
            {businessEntityName}. Once confirmed, this action cannot be undone.
          </p>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onSendRequest}
            disabled={isLoading}
            isLoading={isLoading}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { SendSecureInformationRequestModal };
